/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SystemCleanupPage.tsx
 🗓️ Created: 2025-12-20
 🧩 Description:
    System Cleanup module - list of tenants with cleanup settings.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../hooks/useI18n';
import DataGridSimple from '../../../../components/ui/DataGridSimple';
import { useDataGrid } from '../../../../hooks/useDataGrid';
import { Settings, Edit } from 'lucide-react';
import { useAuthStore } from '../../../../store/auth';
import { listTenants } from '../../../tenants/api/tenantsApi';
import { SimpleTooltip } from '../../../../components/ui/SimpleTooltip';

interface TenantRow {
    id: number;
    uuid?: string;  // External UUID (replaces Sqids)
    name: string;
    slug: string;
    isActive: boolean;
    isPrimary?: boolean;
    cleanupConfig?: {
        enabled?: boolean;
        schedule?: string;
    };
    createdAt: string;
}

export default function SystemCleanupPage() {
    const { t, i18n } = useI18n();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const tenantSlug = user?.tenant?.slug || '';

    // Fetch tenants function for the grid
    const fetchTenantsData = useCallback(async (params: {
        page: number;
        limit: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        search?: string;
    }) => {
        const result = await listTenants(
            tenantSlug,
            Number(user?.id) || 0,
            {
                page: params.page,
                limit: params.limit,
                sortBy: params.sortBy,
                sortOrder: params.sortOrder,
                search: params.search
            }
        );

        if (result.success && Array.isArray(result.data)) {
            return {
                data: result.data,
                total: result.pagination?.total || result.data.length,
                page: result.pagination?.page || params.page,
                limit: result.pagination?.limit || params.limit
            };
        }

        return {
            data: [],
            total: 0,
            page: params.page,
            limit: params.limit
        };
    }, [tenantSlug, user?.id]);

    // Define columns
    const availableColumns = useMemo(() => [
        {
            key: 'name',
            label: t('systemCleanup.columns.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: TenantRow) => (
                <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{item.name}</span>
                    {item.isPrimary && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                            {t('systemCleanup.columns.main')}
                        </span>
                    )}
                </div>
            )
        },
        {
            key: 'cleanupEnabled',
            label: t('systemCleanup.columns.maintenanceState'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: TenantRow) => {
                const isEnabled = item.cleanupConfig?.enabled;
                const schedule = item.cleanupConfig?.schedule || 'none';

                if (isEnabled) {
                    return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {t('systemCleanup.columns.enabled')} ({t(`systemCleanup.options.schedule.${schedule}`)})
                        </span>
                    );
                }
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        {t('systemCleanup.columns.disabled')}
                    </span>
                );
            }
        },
        {
            key: 'isActive',
            label: t('systemCleanup.columns.status'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: TenantRow) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {item.isActive ? t('common.active') : t('common.inactive')}
                </span>
            )
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true
        }
    ], [t, i18n.language]);

    // Use data grid hook
    const {
        data,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
    } = useDataGrid({
        fetchData: fetchTenantsData,
        initialColumns: availableColumns,
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        moduleName: 'systemCleanup'
    });

    // Handle edit - navigate to form
    const handleEdit = useCallback((tenant: TenantRow) => {
        navigate(`/admin/maintenance/system/${tenant.uuid ?? tenant.id}`);
    }, [navigate]);

    // Render actions - only edit button
    const renderActions = useCallback((tenant: TenantRow) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('systemCleanup.actions.configure')} position="top">
                <button
                    onClick={() => handleEdit(tenant)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('systemCleanup.searchPlaceholder')}
                title={t('systemCleanup.title')}
                titleIcon={<Settings className="h-5 w-5 text-primary" />}
                onEdit={handleEdit}
                translations={{
                    items: t('common.items'),
                    columns: t('common.columns'),
                    noItemsFound: t('common.noItemsFound'),
                    showing: t('common.showing'),
                    to: t('common.to'),
                    of: t('common.of'),
                    results: t('common.results'),
                    previous: t('common.previous'),
                    next: t('common.next'),
                    page: t('common.page'),
                    deleteItem: t('common.deleteItem'),
                    areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
                    delete: t('common.delete'),
                    cancel: t('common.cancel'),
                    allSelected: t('common.allSelected'),
                    selected: t('common.selected'),
                    searchOptions: t('common.searchOptions'),
                    noOptionsFound: t('common.noOptionsFound'),
                    selectAll: t('common.selectAll'),
                    clearAll: t('common.clearAll')
                }}
            />
        </div>
    );
}
